/**
 * Zugriff auf die Wartezeiten im Portal.
 *
 * Gerechnet wird ausschliesslich serverseitig; das Portal abonniert nur noch
 * `waitTimes/current`. Der Hook heisst weiterhin `useWaitTimeManager`, damit
 * bestehende Importe unveraendert bleiben — er startet aber keinen Manager mehr,
 * sondern das Firestore-Abo.
 *
 * Konzept: docs/KONZEPT_WARTEZEIT_RUECKBAU_INS_BACKEND.md
 */
import { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  subscribeToWaitTimes,
  unsubscribeFromWaitTimes,
  selectWaitTimes,
  selectNextUnavailable,
  selectWaitTimeMetadata,
  selectIsManagerActive,
  selectWaitTimeLoading,
  selectWaitTimeError,
  selectLastCalculationTime
} from '../redux/slices/waitTimeSlice';

export const useWaitTimeManager = () => {
  const dispatch = useDispatch();

  const waitTimes = useSelector(selectWaitTimes);
  const nextUnavailable = useSelector(selectNextUnavailable);
  const metadata = useSelector(selectWaitTimeMetadata);
  const isActive = useSelector(selectIsManagerActive);
  const isLoading = useSelector(selectWaitTimeLoading);
  const error = useSelector(selectWaitTimeError);
  const lastCalculationTime = useSelector(selectLastCalculationTime);

  const startManager = useCallback(async () => {
    try {
      return dispatch(subscribeToWaitTimes());
    } catch (err) {
      console.error('useWaitTimeManager: Fehler beim Start des Abos:', err);
      throw err;
    }
  }, [dispatch]);

  const stopManager = useCallback(async () => {
    try {
      dispatch(unsubscribeFromWaitTimes());
    } catch (err) {
      console.error('useWaitTimeManager: Fehler beim Beenden des Abos:', err);
      throw err;
    }
  }, [dispatch]);

  // Kein Cleanup bei Unmount: Das Abo gehoert zur Sitzung, nicht zur Komponente
  // (der AppProvider haelt es), und es wird beim Logout beendet.
  useEffect(() => () => {}, []);

  return {
    waitTimes,
    nextUnavailable,
    metadata,
    isActive,
    isLoading,
    error,
    lastCalculationTime,
    startManager,
    stopManager
  };
};

// Hook für nur die Wartezeitdaten (für UI-Komponenten)
export const useWaitTimes = () => {
  const waitTimes = useSelector(selectWaitTimes);
  const nextUnavailable = useSelector(selectNextUnavailable);
  const metadata = useSelector(selectWaitTimeMetadata);
  const isActive = useSelector(selectIsManagerActive);
  const error = useSelector(selectWaitTimeError);

  return {
    waitTimes,
    nextUnavailable,
    metadata,
    isActive,
    error,
    isCalculating: !isActive || waitTimes.general === "Berechnung..."
  };
};

export default useWaitTimeManager;
